import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Building2, Search, Loader2, MapPin, Layers, ChevronRight } from "lucide-react";
import { getAdminProjects, getAdminProjectFilters } from "../../services/adminProjects";
import PageHeader from "../../components/PageHeader";
import PaginationControls from "../../components/PaginationControls";

const PAGE_SIZE = 24;
const asList = (r) => (Array.isArray(r) ? r : r?.items || []);

const STATUS = {
  ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  COMPLETED: "bg-slate-100 text-slate-600 border-slate-200",
};

/**
 * Read-only Projects browser for Zumale/BuildVizer admins — every project across all onboarded
 * developers, filterable by developer / city / active-completed. Rows open a read-only detail with
 * phases → properties. No create/edit anywhere.
 */
const AdminProjects = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ developers: [], cities: [] });
  const [developerId, setDeveloperId] = useState("");
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, totalPages: 0, hasNext: false, hasPrevious: false });
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    getAdminProjectFilters()
      .then((r) => setFilters({ developers: asList(r?.developers), cities: r?.cities || [] }))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (developerId) params.developerId = developerId;
      if (city) params.city = city;
      if (status) params.status = status;
      if (search.trim()) params.search = search.trim();
      const r = await getAdminProjects(params);
      setData({
        items: asList(r),
        total: r?.total || 0,
        totalPages: r?.totalPages || 0,
        hasNext: !!r?.hasNext,
        hasPrevious: !!r?.hasPrevious,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load projects");
    } finally {
      setLoading(false);
    }
  }, [developerId, city, status, search, page]);

  // debounce search; reset to page 1 when any filter changes
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(load, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [load]);

  const resetPageThen = (setter) => (v) => {
    setPage(1);
    setter(v);
  };

  const open = (p) => navigate(`/zumale-admin/projects/${p.projectId}?customerId=${p.customerId}`);

  const hasFilters = developerId || city || status || search;

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Building2}
        title="Projects"
        subtitle={`${data.total} project${data.total === 1 ? "" : "s"} across onboarded developers — read-only`}
      >
        {/* Filter bar — solid-white controls floating on the gradient band */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => resetPageThen(setSearch)(e.target.value)}
              placeholder="Search projects…"
              className="w-full sm:w-56 rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 placeholder-slate-400 focus:border-flamingo-500 focus:outline-none focus:ring-1 focus:ring-flamingo-200"
            />
          </div>
          <select value={developerId} onChange={(e) => resetPageThen(setDeveloperId)(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-flamingo-500 focus:outline-none">
            <option value="">All developers</option>
            {filters.developers.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select value={city} onChange={(e) => resetPageThen(setCity)(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-flamingo-500 focus:outline-none">
            <option value="">All cities</option>
            {filters.cities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <select value={status} onChange={(e) => resetPageThen(setStatus)(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-flamingo-500 focus:outline-none">
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
          </select>
          {hasFilters && (
            <button
              onClick={() => { setPage(1); setDeveloperId(""); setCity(""); setStatus(""); setSearch(""); }}
              className="rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/10 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </PageHeader>

      {loading ? (
        <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading projects…</div>
      ) : data.items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">No projects match these filters.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((p) => (
              <button key={p.projectId} onClick={() => open(p)} className="group rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-flamingo-200 hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <p className="truncate text-sm font-semibold text-slate-800">{p.projectName}</p>
                  <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS[p.status] || STATUS.ACTIVE}`}>
                    {p.status === "COMPLETED" ? "Completed" : "Active"}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-slate-500">{p.developerName}</p>
                <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                  {p.city ? <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.city}</span> : null}
                  <span className="inline-flex items-center gap-1"><Layers size={12} /> {p.phaseCount} phase{p.phaseCount === 1 ? "" : "s"}</span>
                  <span className="ml-auto inline-flex items-center gap-0.5 text-flamingo-600 opacity-0 group-hover:opacity-100">View <ChevronRight size={13} /></span>
                </div>
              </button>
            ))}
          </div>

          {data.totalPages > 1 && (
            <PaginationControls
              pagination={{ page, pageSize: PAGE_SIZE, total: data.total, totalPages: data.totalPages, hasNext: data.hasNext, hasPrevious: data.hasPrevious }}
              onPageChange={setPage}
              itemName="projects"
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminProjects;
